/** Fixed palette for deterministic avatars (brand-adjacent greens/teals/neutrals). */
export const USER_AVATAR_PALETTE = [
  "#263833",
  "#30a46c",
  "#0d9488",
  "#1a5c4a",
  "#4a6259",
  "#288b5b",
  "#0f766e",
  "#3d524a",
] as const;

export interface ResolveUserAvatarInput {
  email?: string | null;
  userId?: string | null;
}

export interface UserAvatar {
  letter: string;
  color: string;
  title?: string;
}

export interface ResolveUploaderEmailOptions {
  currentUserId?: string | null;
  currentUserEmail?: string | null;
  /** Map of Entra oid → email from platform users. */
  emailByUserId?: Record<string, string>;
}

/** First alphanumeric of the email local-part, uppercased; `?` when missing. */
export function initialFromEmail(email: string | null | undefined): string {
  const trimmed = (email ?? "").trim();
  if (!trimmed) return "?";
  const local = trimmed.includes("@") ? trimmed.slice(0, trimmed.indexOf("@")) : trimmed;
  const match = local.match(/[a-zA-Z0-9]/);
  return match ? match[0]!.toUpperCase() : "?";
}

/** Deterministic colour from a stable key (prefer userId). */
export function avatarColorFromKey(key: string): string {
  const normalized = key.trim();
  if (!normalized) return USER_AVATAR_PALETTE[0]!;
  let hash = 0;
  for (let i = 0; i < normalized.length; i++) {
    hash = (hash * 31 + normalized.charCodeAt(i)) >>> 0;
  }
  return USER_AVATAR_PALETTE[hash % USER_AVATAR_PALETTE.length]!;
}

/**
 * Resolve display email for an uploader id/email string.
 * - Strings with `@` are treated as legacy email `uploaded_by` values.
 * - Matching current user id uses current user email.
 * - Otherwise look up oid in `emailByUserId`.
 */
export function resolveUploaderEmail(
  uploadedBy: string | null | undefined,
  options: ResolveUploaderEmailOptions = {}
): string | null {
  const key = (uploadedBy ?? "").trim();
  if (!key) return null;
  if (key.includes("@")) return key;

  const currentId = (options.currentUserId ?? "").trim();
  const currentEmail = (options.currentUserEmail ?? "").trim();
  if (currentId && key === currentId && currentEmail) {
    return currentEmail;
  }

  const mapped = options.emailByUserId?.[key]?.trim();
  return mapped || null;
}

/** Letter from email + colour from userId (fallback email). */
export function resolveUserAvatar({ email, userId }: ResolveUserAvatarInput): UserAvatar {
  const id = (userId ?? "").trim();
  const mail = (email ?? "").trim();
  const letter = initialFromEmail(mail);
  const color = avatarColorFromKey(id || mail);
  return {
    letter,
    color,
    title: mail || id || undefined,
  };
}
